import { PlatformEnum } from '@fluenceur/types';
import type { ContactPlatform } from './contact-drawer-url-state';

/**
 * Map a social platform (a {@link PlatformEnum} value or a raw string) to the
 * contact drawer's platform key.
 *
 * Instagram and TikTok are the only platforms the drawer renders, so every
 * other value — including `null`/`undefined` or an unsupported platform such as
 * YouTube — falls back to Instagram (`'ig'`), which matches the drawer's
 * URL-state default. `PlatformEnum.TikTok` equals the lowercase string
 * `'tiktok'`, so the comparison handles both the enum and a raw (case-
 * insensitive) string without duplicate branches.
 */
export const platformOf = (platform: PlatformEnum | string | null | undefined): ContactPlatform =>
  String(platform ?? '').toLowerCase() === PlatformEnum.TikTok ? 'tiktok' : 'ig';

/**
 * Inverse of {@link platformOf}: map the drawer's platform key back to the
 * canonical {@link PlatformEnum} the wishlist / provisioning APIs expect.
 *
 * Only Instagram and TikTok are addressable in the drawer, so `'tiktok'` maps to
 * {@link PlatformEnum.TikTok} and everything else (i.e. `'ig'`) to
 * {@link PlatformEnum.Instagram}, matching the URL-state default. Shared so the
 * favorite star and the "save this creator" action derive the platform from a
 * single source and can never disagree.
 */
export const platformEnumOf = (platform: ContactPlatform): PlatformEnum =>
  platform === 'tiktok' ? PlatformEnum.TikTok : PlatformEnum.Instagram;

/**
 * Pick the platform the drawer should actually display.
 *
 * The URL carries the platform the user clicked (`requested`), which may not be a
 * platform the resolved contact actually has — e.g. a TikTok-only affiliate opened
 * with the URL default `'ig'`. Showing the requested platform then lands on an
 * empty state under a disabled toggle. So display the requested platform when the
 * contact has a profile on it, otherwise fall back to the contact's first
 * available platform (its rail summary rows).
 *
 * A contact with no summary rows yet (an unsaved discovery creator) keeps the
 * requested platform, so its "save this creator" prompt stays reachable rather
 * than being masked by a fallback.
 *
 * Display-only: callers must NOT rewrite the URL from this, so a shared link
 * still opens on the platform it named once the contact does have that profile.
 */
export const effectiveContactPlatform = (
  requested: ContactPlatform,
  availablePlatforms: ContactPlatform[]
): ContactPlatform =>
  availablePlatforms.length === 0 || availablePlatforms.includes(requested)
    ? requested
    : availablePlatforms[0];
